import CompletionProgressCard from '~/statistics-v2/components/CompletionProgressCard'
import MasteryCertificatesProgress from '~/statistics-v2/components/MasteryCertificatesProgress'
import MustKnowsProgressCard from '~/statistics-v2/components/MustKnowProgress'
import OverallProgressCard from '~/statistics-v2/components/OverallProgressCard'
import TopicsOverview from '~/statistics-v2/components/TopicsOverview'
import {
  MasteryCertificate,
  MustKnowsProgressCardProps,
  OverallProgressCardProps,
  TopicStats,
} from '~/statistics-v2/types/statistics.types'

export interface StatsBodyProps {
  certificates: MasteryCertificate[]
  /** True below grade 6, where Must-Knows do not exist. Hides the card and the topic column. */
  hideMustKnows: boolean
  isLoading: boolean
  isSmallScreen: boolean
  mustKnowProgressCardProps: MustKnowsProgressCardProps
  onSelectTopic: (topicStats: TopicStats) => void
  overallProgressCardProps: OverallProgressCardProps
  selectedGrade: { key: string; label: string }
  /** False for whole-curriculum selections — topics live under one grade's subdivisions. */
  showTopics: boolean
  topicsStats: TopicStats[]
}

/**
 * Progress cards, mastery certificates and the topic list — everything below the profile panel.
 *
 * Props only; the page supplies the figures from whichever stats hook it uses.
 *
 * ⚠ Certificates and topics wait on `isLoading` while the progress cards render through it. The
 * cards have skeletons and these two do not, so rendering them early shows an empty certificate row.
 */
export default function StatsBody({
  certificates,
  hideMustKnows,
  isLoading,
  isSmallScreen,
  mustKnowProgressCardProps,
  onSelectTopic,
  overallProgressCardProps,
  selectedGrade,
  showTopics,
  topicsStats,
}: StatsBodyProps): React.ReactElement {
  return (
    <>
      <div className="flex w-full max-w-screen-19/20 flex-col justify-between gap-3 rounded-6 bg-purple-plum p-2 md:p-4">
        <div className="flex w-full flex-col items-center gap-3 lg:flex-row">
          <OverallProgressCard
            isLoading={isLoading}
            overallProgressCardProps={overallProgressCardProps}
            selectedGrade={selectedGrade}
          />
          <CompletionProgressCard isLoading={isLoading} overallProgressCardProps={overallProgressCardProps} />
          {!hideMustKnows && (
            <MustKnowsProgressCard isLoading={isLoading} mustKnowProgressCardProps={mustKnowProgressCardProps} />
          )}
        </div>
      </div>
      {!isLoading && (
        <>
          <MasteryCertificatesProgress
            certificates={certificates}
            isAllGrades={selectedGrade.key === '0'}
            isSmallScreen={isSmallScreen}
          />
          {showTopics && (
            <TopicsOverview combinedTopics={topicsStats} hideMustKnows={hideMustKnows} onSelectTopic={onSelectTopic} />
          )}
        </>
      )}
    </>
  )
}
